import { useState } from 'react';
import { AlertTriangle, Check, ChevronDown, EyeOff, Info } from 'lucide-react';

import { SoftCard } from '@/components/ui/soft-card';
import { Toggle } from '@/components/ui/toggle';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const HELP_ARTICLE = 'https://unify.helpkit.so/inventory/7JASpDe6Fw24Gk9K36f1oP/how-to-hide-a-product/7MhDo5Gbmb4GXWaGEdRKvj';

function StateIcon({ state }) {
	if (state === 'visible') {
		return (
			<span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-success-soft text-success-soft-foreground">
				<Check className="h-3 w-3" />
			</span>
		);
	}
	if (state === 'blocked') {
		return (
			<span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-warning-soft text-warning-soft-foreground">
				<AlertTriangle className="h-3 w-3" />
			</span>
		);
	}
	return (
		<span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
			<EyeOff className="h-3 w-3" />
		</span>
	);
}

/**
 * Sidebar card summarising where this product is live. The header (count pill,
 * summary line, Active toggle) stays visible; the per-channel breakdown and the
 * channel editor sit behind the chevron so a shop on many channels doesn't
 * push the Issues and delivery cards off-screen.
 */
export function VisibilityCard({ channelStates, hidden, togglingActive, onToggleActive, canEditChannels, onEditChannels, onFix }) {
	const [open, setOpen] = useState(false);

	const visibleCount = channelStates.filter((c) => c.state === 'visible').length;
	// Count 'blocked' explicitly rather than everything-that-isn't-visible: a
	// channel the retailer switched off (and every channel on a hidden product)
	// is 'hidden', not waiting on a fix, so the amber half would otherwise
	// promise a remedy this page doesn't have. Same rule the Issues card uses,
	// and the same one computeVisibilityCounts applies on the Manage list.
	const attentionCount = channelStates.filter((c) => c.state === 'blocked').length;

	return (
		<SoftCard className="!p-5">
			<div className="flex items-start justify-between gap-3">
				<div className="min-w-0">
					<div className="flex items-center gap-1.5">
						<h3 className="text-base font-bold">Visibility</h3>
						<TooltipProvider delayDuration={150}>
							<Tooltip>
								<TooltipTrigger asChild>
									<a
										href={HELP_ARTICLE}
										target="_blank"
										rel="noopener noreferrer"
										aria-label="How to hide a product"
										className="inline-flex items-center rounded-full text-muted-foreground transition hover:text-foreground"
									>
										<Info className="h-3.5 w-3.5" />
									</a>
								</TooltipTrigger>
								<TooltipContent side="top" className="max-w-xs text-xs leading-relaxed">
									You can hide/unhide this product across all channels by switching on/off the toggle. Or manage
									visibility by clicking 'Edit channel visibility' below.
								</TooltipContent>
							</Tooltip>
						</TooltipProvider>
						{channelStates.length > 0 ? (
							<span
								data-testid="visibility-count-pill"
								className="inline-flex shrink-0 items-center overflow-hidden rounded-full ring-1 ring-border/60"
							>
								<span className="inline-flex items-center gap-1 bg-success-soft px-2 py-0.5 text-success-soft-foreground">
									<Check className="h-3 w-3" />
									<span className="text-xs font-bold tabular-nums">{visibleCount}</span>
								</span>
								{attentionCount > 0 ? (
									<span className="inline-flex items-center gap-1 bg-warning-soft px-2 py-0.5 text-warning-soft-foreground">
										<AlertTriangle className="h-3 w-3" />
										<span className="text-xs font-bold tabular-nums">{attentionCount}</span>
									</span>
								) : null}
							</span>
						) : null}
					</div>
					<p className="mt-1 text-sm text-muted-foreground">
						{!channelStates.length
							? 'This product is not on any channels yet.'
							: hidden
								? 'This product is hidden from all channels.'
								: `This product is visible in ${visibleCount} of ${channelStates.length} channel${channelStates.length === 1 ? '' : 's'}.`}
					</p>
				</div>
				{channelStates.length > 0 ? (
					<button
						type="button"
						onClick={() => setOpen((o) => !o)}
						aria-expanded={open}
						aria-label={open ? 'Hide channel details' : 'Show channel details'}
						className="mt-1 shrink-0 text-muted-foreground transition hover:text-foreground"
					>
						<ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
					</button>
				) : null}
			</div>

			<div className="mt-3">
				<Toggle checked={!hidden} onChange={onToggleActive} disabled={togglingActive} label={hidden ? 'Hidden' : 'Active'} />
			</div>

			{open && channelStates.length > 0 ? (
				<div className="mt-4">
					<div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Per channel</div>
					<ul className="space-y-1">
						{channelStates.map(({ id, label, state, fixableIssues }) => (
							<li key={id} data-testid={`channel-row-${id}`} data-state={state} className="py-1.5">
								<div className="flex items-center justify-between gap-3">
									<div className="flex min-w-0 items-center gap-2.5">
										<StateIcon state={state} />
										<div
											className={`text-sm font-semibold ${state === 'hidden' ? 'text-muted-foreground' : 'text-foreground'}`}
										>
											{label}
										</div>
									</div>
									{state === 'blocked' && fixableIssues.length > 0 ? (
										<button
											type="button"
											onClick={() => onFix(id, fixableIssues)}
											className="inline-flex h-7 shrink-0 items-center rounded-full bg-primary px-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90"
										>
											Fix
										</button>
									) : null}
								</div>
							</li>
						))}
					</ul>
					{canEditChannels ? (
						<button
							type="button"
							onClick={onEditChannels}
							className="mt-4 inline-flex w-full items-center justify-center rounded-full bg-muted px-4 py-2 text-sm font-semibold text-foreground transition hover:bg-accent"
						>
							Edit channel visibility
						</button>
					) : null}
				</div>
			) : null}
		</SoftCard>
	);
}
